import React from 'react';
import { Helmet } from '@vuer-ai/react-helmet-async';
import { LegalPageLayout } from './LegalPageLayout';

const PRIVACY_EMAIL = 'support@studiovaultbeta.com';

export const PrivacyPage: React.FC = () => (
  <>
    <Helmet>
      <title>Privacy Policy — StudioVault</title>
      <meta
        name="description"
        content="How StudioVault collects, uses, protects, and shares personal information."
      />
    </Helmet>
    <LegalPageLayout
      dateLabel="UPDATED OCTOBER 13, 2026"
      eyebrow="PRIVACY"
      title="Your work stays yours."
      summary="This policy explains what information StudioVault handles when you use our website, desktop application, and cloud service, and the choices available to you."
    >
      <section>
        <h2>1. Who is responsible</h2>
        <p>
          StudioVault is responsible for the personal information described in
          this policy. Questions and privacy requests can be sent to{' '}
          <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>.
        </p>
        <p>
          This policy applies to StudioVault&apos;s website, desktop
          application, cloud service, and related support. It does not govern
          websites or services operated by other companies.
        </p>
      </section>

      <section>
        <h2>2. Information we handle</h2>
        <h3>Account information</h3>
        <p>
          We handle information such as your name, artist name, email address,
          account identifiers, profile image, authentication status, and the
          information needed to keep your account secure. If you sign in with
          Google, Google provides the profile and account information you
          authorize it to share.
        </p>

        <h3>Projects and collaboration</h3>
        <p>
          The service stores the projects and files you choose to upload,
          including audio, session files, samples, renders, project names,
          version history, notes, detected plug-in information, waveforms,
          boards, invitations, and collaborator roles. Other members of a
          project or board may provide information about you when they invite
          you or collaborate with you.
        </p>

        <h3>Public community contributions and moderation</h3>
        <p>
          Community library uploads are shared with others, including your
          creator name, account identifier, descriptions, tags, artwork, and
          audio previews. Do not put private information in public
          contributions. Recipients can retain or redistribute downloaded copies
          under the rights actually granted.
        </p>
        <p>
          We record sharing consent, policy version, time, account and file
          revision identifiers, file hashes, reports, appeals, restrictions, and
          moderation decisions to operate the library, handle disputes, and
          prevent abuse. Reports and internal decision history are restricted to
          authorized moderators; creators can see reasons affecting their own
          uploads. Relevant complaint or counter-notice information may be
          disclosed to the other party or authorities where needed for the legal
          process. These records may be retained after removal when necessary
          for those purposes, subject to the retention rules below.
        </p>

        <h3>Desktop and technical information</h3>
        <p>
          The desktop application accesses folders you select or authorize so it
          can identify supported sessions, detect changes, and transfer the
          files you ask it to sync. Local folder locations and similar desktop
          settings may remain on your device. We also receive ordinary service
          information such as IP address, device and operating-system details,
          application version, request timestamps, and error or security logs.
        </p>

        <h3>Support information</h3>
        <p>
          If you contact us, we handle the message, contact details, and any
          files or diagnostic information you choose to include.
        </p>
      </section>

      <section>
        <h2>3. Why we use information</h2>
        <p>We use information to:</p>
        <ul>
          <li>create and secure accounts and authenticate users;</li>
          <li>store, sync, version, restore, and display project content;</li>
          <li>support invitations, permissions, boards, and collaboration;</li>
          <li>operate downloads, releases, updates, and customer support;</li>
          <li>
            protect the service, prevent misuse, and diagnose failures; and
          </li>
          <li>meet legal obligations and enforce our Terms.</li>
        </ul>
        <p>
          Where a legal basis is required, we process information as necessary
          to provide the service you request and perform our contract with you;
          to comply with law; and for legitimate interests such as securing,
          maintaining, and improving the service. Where consent is the required
          basis, you may withdraw it at any time without affecting earlier
          processing.
        </p>
      </section>

      <section>
        <h2>4. When information is disclosed</h2>
        <p>We disclose information only as needed to:</p>
        <ul>
          <li>
            provide the service through infrastructure providers that host the
            application, database, project storage, and release files;
          </li>
          <li>
            complete sign-in through Google and other authentication providers
            you choose;
          </li>
          <li>
            show project or board information to people you invite, according to
            the permissions you assign;
          </li>
          <li>
            work with professional advisers, respond to lawful requests, or
            protect users, StudioVault, and the public; or
          </li>
          <li>
            complete a financing, acquisition, reorganization, or sale, subject
            to appropriate confidentiality protections.
          </li>
        </ul>
        <p>
          Our current infrastructure includes Amazon Web Services for object
          storage, MongoDB for database services, Render for application
          hosting, Netlify for website delivery, and Google for optional
          sign-in. Their handling of information is governed by their own terms
          and our arrangements with them.
        </p>
        <p>
          <strong>
            StudioVault does not sell personal information or share it for
            cross-context behavioral advertising.
          </strong>{' '}
          We do not make decisions that produce legal or similarly significant
          effects using automated profiling.
        </p>
      </section>

      <section>
        <h2>5. International use and transfers</h2>
        <p>
          StudioVault can be used from different countries. We and our service
          providers may process information in the United States and other
          places where we or they operate. Privacy laws in those places may
          differ from those where you live. Where applicable law requires a
          transfer mechanism, we use an approved mechanism or another lawful
          basis for the transfer. Contact us to ask about the safeguards
          relevant to your information.
        </p>
      </section>

      <section>
        <h2>6. Retention</h2>
        <p>
          We keep account and project information while your account is active
          and as needed to provide the service. If you delete a project or ask
          us to close your account, we delete or de-identify associated
          information unless we need to retain it for security, fraud
          prevention, legal compliance, dispute resolution, or the protection of
          legal rights. Information may remain for a limited period in backups
          and system logs before being overwritten.
        </p>
        <p>
          We determine retention periods from the type and sensitivity of the
          information, the reason it was collected, operational backup cycles,
          and applicable legal requirements.
        </p>
      </section>

      <section>
        <h2>7. Security and your role</h2>
        <p>
          We use administrative, technical, and organizational measures designed
          to protect information, including access controls, encrypted network
          transport, restricted project permissions, and time-limited file
          transfer links. No service can guarantee absolute security. Use a
          secure device, protect your sign-in credentials, and keep an
          independent copy of important project files.
        </p>
      </section>

      <section>
        <h2>8. Your choices and rights</h2>
        <p>
          Depending on where you live, you may have rights to access, correct,
          delete, or receive a copy of your personal information; restrict or
          object to certain processing; withdraw consent; or appeal a denied
          request. You may also have the right to complain to your local data
          protection authority. You will not be discriminated against for
          exercising a privacy right.
        </p>
        <p>
          <strong>
            You may object to processing based on legitimate interests by
            contacting us.
          </strong>{' '}
          Send requests to{' '}
          <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>. We may need
          information to verify your identity and authority before completing a
          request. Authorized agents may submit requests where local law allows.
        </p>
      </section>

      <section>
        <h2>9. California notice</h2>
        <p>
          In the preceding 12 months, StudioVault has collected the categories
          described above, which may include identifiers; customer-record
          information; internet or other electronic activity; commercial
          information about a subscription; and audio, visual, and user-created
          content. We collect these categories from you, your collaborators,
          your device, and service providers, and use and disclose them for the
          purposes described in this policy.
        </p>
        <p>
          We do not sell these categories or share them for cross-context
          behavioral advertising. We do not use sensitive personal information
          to infer characteristics about you. California residents may request
          access to specific information, correction, deletion, or portability
          as provided by law by using the contact method in Section 8.
        </p>
      </section>

      <section>
        <h2>10. Local storage and external services</h2>
        <p>
          StudioVault uses browser or application storage to keep you signed in
          and remember settings such as theme preference. We do not currently
          use advertising cookies. Google and other services you choose may use
          their own cookies or storage when providing sign-in or linked
          services.
        </p>
      </section>

      <section>
        <h2>11. Children</h2>
        <p>
          StudioVault is not intended for children under 16, and we do not
          knowingly collect their personal information. If you believe a child
          has provided information to us, contact us so we can review and remove
          it where appropriate.
        </p>
      </section>

      <section>
        <h2>12. Changes to this policy</h2>
        <p>
          We may update this policy as the service or applicable law changes. We
          will post the updated policy with a new effective date and provide
          additional notice when a change materially affects your rights or how
          we use personal information.
        </p>
      </section>
    </LegalPageLayout>
  </>
);

export default PrivacyPage;
